/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable default-case */
/* eslint-disable no-unreachable */
import { CustomPageHeader, Text } from '@src/components';
import MarketBalanceInformation from '@src/pages/ticketMarket/balanceUpload/balanceInformation';
import ComplimentsBalanceInformation from '@src/pages/ticketCompliments/balanceUpload/balanceInformation';
import { useCallback, useEffect, useState, Suspense, lazy } from 'react';
import { Steps } from 'antd';
import {
    getById,
    getCurrentLoadBalance,
    loadBalancesUpdate,
    setActiveStep,
} from '@src/store/slice/balanceUploadSlice';
import { useDispatch, useSelector } from 'react-redux';
import '@src/styles/balanceUpload/balanceUpload.scss';
import {
    getLoadBalanceDetailsGetPagedList,
    setDetailTableFilterClear,
} from '@src/store/slice/balanceUploadDetailSlice';
import { useLocation } from 'react-router-dom';
import { handleAuthStatus, isCreditCardUser } from '@src/utils/utils';
import { getFaceValues } from '@src/store/slice/faceValuePopupSlice';
import Payment from '@src/pages/ticketRestaurant/balanceUpload/payment/index';
import OrderSummary from '@src/pages/ticketRestaurant/balanceUpload/orderSummary/index';
import Completed from '@src/pages/ticketRestaurant/balanceUpload/Completed';
import { delayPopup, getProgressDot } from '@src/pages/ticketRestaurant/balanceUpload/index';
import { VouIdCode } from '@src/store/enums';
import { getCustomerVouListForGift } from '@src/store/slice/customerSlice';
import propTypes from 'prop-types';

const FaceValuePopup = lazy(() =>
    delayPopup(import('../../ticketRestaurant/faceValuePopup/faceValuePopup')),
);

const { Step } = Steps;

const activeStepKeys = ['balanceInformation', 'orderSummary', 'payment', 'approval'];

const BalanceUpload = ({ vouId = VouIdCode.Market, routes = ['ticketMarketHeader'] }) => {
    const { activeStep } = useSelector((state) => state?.balanceUpload);
    const { customerVou, selectedCustomer } = useSelector((state) => state?.customer);
    const [modalVisible, setModalVisible] = useState(true);
    const dispatch = useDispatch();
    const location = useLocation();
    const [pageStep, setPageStep] = useState([]);
    const { userAuthorizationMarketStatus, userAuthorizationGiftStatus } = useSelector(
        (state) => state?.user,
    );
    const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);
    const { faceValues } = useSelector((state) => state?.faceValuePopup);

    useEffect(() => {
        dispatch(setDetailTableFilterClear());
    }, [dispatch]);

    useEffect(async () => {
        await dispatch(getFaceValues());
    }, [dispatch]);

    useEffect(async () => {
        const search = new URLSearchParams(location?.search);
        const balanceId = search.get('balanceId');
        if (!balanceId && !location.state?.id) {
            await dispatch(getCurrentLoadBalance({ vouId }));
        } else if (location?.state?.id) {
            await dispatch(getById(location?.state?.id));
        } else {
            await dispatch(getById(balanceId));
        }
    }, [dispatch, routes, location]);

    const gotoSummaryPage = useCallback(async () => {
        await dispatch(
            loadBalancesUpdate({
                ...currentLoadBalance,
                orderStatus: 10,
                vouId,
            }),
        );
    }, [currentLoadBalance, dispatch, vouId]);

    const stepChange = useCallback(
        async (current) => {
            if (activeStep - 1 === current) {
                if (current === 0) {
                    await dispatch(
                        getLoadBalanceDetailsGetPagedList({
                            body: {
                                loadBalanceId: currentLoadBalance?.id,
                                showOnlyPriced: false,
                                vouId,
                            },
                            pageNumber: 1,
                            pageSize: 10000,
                            isFilter: false,
                        }),
                    );
                }
                await dispatch(
                    loadBalancesUpdate({
                        ...currentLoadBalance,
                        orderStatus:
                            currentLoadBalance.orderStatus - 10 >= 0 ? currentLoadBalance.orderStatus - 10 : 0,
                        vouId,
                    }),
                );
            }
        },
        [activeStep, currentLoadBalance, dispatch, vouId],
    );

    const BalanceInformation = vouId === VouIdCode.Market ? MarketBalanceInformation : ComplimentsBalanceInformation;

    const steps = [
        { title: <Text t="balanceInformation2" />, content: BalanceInformation, key: 'balanceInformation' },
        { title: <Text t="orderSummary" />, content: OrderSummary, key: 'orderSummary' },
        { title: <Text t="payment" />, content: Payment, key: 'payment' },
        { title: <Text t="approval" />, content: Completed, key: 'approval' },
    ];

    const StepContent = steps.find((item) => item.key === activeStepKeys[activeStep])?.content || <> </>;

    useEffect(() => {
        if (!pageStep || pageStep.length === 0) setPageStep(steps);
    }, [vouId]);

    useEffect(async () => {
        if (!customerVou?.pamId) return;

        const giftCustomerInfo = await dispatch(
            getCustomerVouListForGift({
                customerId: selectedCustomer?.customerId,
                vouId,
            }),
        );

        const userAuthorizationStatus =
            vouId === VouIdCode.Market ? userAuthorizationMarketStatus : userAuthorizationGiftStatus;

        if (
            !isCreditCardUser(giftCustomerInfo?.payload?.data[0]) ||
            handleAuthStatus(userAuthorizationStatus || [], 'ApprovalAuthorizationGroup') ||
            handleAuthStatus(userAuthorizationStatus || [], 'PaymentAuthorizationGroup')
        ) {
            if (activeStepKeys[activeStep] === 'payment') {
                await gotoSummaryPage();
                await dispatch(setActiveStep(1));
            }

            const withoutPaymentTab = stepsArr.filter((item) => item.key !== 'payment');
            if (withoutPaymentTab.length === stepsArr.length || withoutPaymentTab.length !== 3) return;
            setPageStep([...withoutPaymentTab]);
        }
    }, [customerVou, userAuthorizationMarketStatus, userAuthorizationGiftStatus]);

    const changeTab = (key) => {
        if (window.history.pushState) {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('page', key);
            window.history.pushState({ path: newUrl.href }, '', newUrl.href);
        }
    };

    const isShowTabName = () => {
        switch (activeStepKeys[activeStep]) {
            case 'balanceInformation':
                return changeTab('orderform');
            case 'orderSummary':
                return changeTab('ordersummary');
            case 'payment':
                return changeTab('orderpayment');
            case 'approval':
                return changeTab('orderresult');
        }
    };

    useEffect(() => {
        isShowTabName();
    }, [activeStepKeys[activeStep]]);

    return (
        <CustomPageHeader
            data-testid="comp-balance-upload"
            title={<Text t="balanceUpload" />}
            showBreadCrumb
            showHelpButton
            routes={routes}
        >
            <div className="row">
                <Steps
                    onChange={stepChange}
                    current={activeStep}
                    className="card-order-steps"
                    labelPlacement="vertical"
                    progressDot={getProgressDot()}
                >
                    {pageStep.map((item, index) => (
                        <Step key={item.title + index.toString()} title={item.title} disabled={activeStep === 3} />
                    ))}
                </Steps>
            </div>
            <StepContent vouId={vouId} isShared />
            {setActiveStep && (
                <Suspense fallback={<Steps />}>
                    {faceValues?.htmlType < 6 &&
                        (!faceValues?.isMainPageAccepted || faceValues?.isMainPageClosed) &&
                        !faceValues?.isClosed &&
                        Object.keys(faceValues)?.length > 0 && (
                            <FaceValuePopup
                                modalVisible={modalVisible}
                                handleModalVisible={setModalVisible}
                                faceValues={faceValues}
                                isMainPage={false}
                            />
                        )}
                </Suspense>
            )}
        </CustomPageHeader>
    );
};

BalanceUpload.propTypes = {
    vouId: propTypes.string.isRequired,
    routes: propTypes.array,
};

export default BalanceUpload;
